/**
 * StatCard - IBKD规范统计卡片组件
 *
 * 支持 title / label 两种传参（label 为 title 别名）
 * 支持 color 自定义强调色
 */
import type { ReactNode } from 'react'
import { clsx } from 'clsx'
import { TrendingUp, TrendingDown } from 'lucide-react'

const COLOR_MAP: Record<string, { bg: string; text: string }> = {
  blue:    { bg: 'bg-blue-50',    text: 'text-blue-600'    },
  green:   { bg: 'bg-green-50',   text: 'text-green-600'   },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-600'   },
  orange:  { bg: 'bg-orange-50',  text: 'text-orange-600'  },
  indigo:  { bg: 'bg-indigo-50',  text: 'text-indigo-600'  },
  red:     { bg: 'bg-red-50',     text: 'text-red-600'     },
  purple:  { bg: 'bg-purple-50',  text: 'text-purple-600'  },
  teal:    { bg: 'bg-teal-50',    text: 'text-teal-600'    },
  violet:  { bg: 'bg-violet-50',  text: 'text-violet-600'  },
  rose:    { bg: 'bg-rose-50',    text: 'text-rose-600'    },
}

export interface StatCardProps {
  /** 卡片标题（与 label 二选一） */
  title?: string
  /** 卡片标题别名 */
  label?: string
  value: string | number | ReactNode
  icon?: React.ReactNode
  /** 强调色 */
  color?: 'blue' | 'green' | 'emerald' | 'amber' | 'orange' | 'indigo' | 'red' | 'purple' | 'teal' | 'violet' | 'rose'
  trend?: {
    value: number
    label?: string
  }
  footer?: React.ReactNode
  className?: string
  compactOnMobile?: boolean
  /** 紧凑模式：更低高度、更小字号，适合仪表盘多列 KPI */
  compact?: boolean
  /** 与主值同卡、靠右展示（如「当前筛选」统计） */
  sideLabel?: string
  sideValue?: string | number
  /** 传入时整卡可点击（原生 button，便于键盘与无障碍） */
  onClick?: () => void
}

export function StatCard({
  title,
  label,
  value,
  icon,
  color,
  trend,
  footer,
  className,
  compactOnMobile = true,
  compact = false,
  sideLabel,
  sideValue,
  onClick,
}: StatCardProps) {
  const displayTitle = title ?? label ?? ''
  const isPositive = trend && trend.value >= 0
  const colorCfg = color ? COLOR_MAP[color] : null
  const showSide = sideValue !== undefined && sideValue !== null

  const rootClassName = clsx(
    'rounded-lg bg-white shadow-card dark:border dark:border-[#3b434e] dark:bg-slate-800 dark:shadow-none',
    compact && 'flex h-full min-h-0 flex-col p-2.5 sm:p-3',
    !compact && 'p-6',
    !compact && compactOnMobile && 'p-4 md:p-6',
    onClick &&
      'w-full cursor-pointer border-0 text-left font-inherit transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900',
    className
  )

  const inner = (
    <>
      <div
        className={clsx(
          'flex justify-between',
          compact ? 'min-h-0 flex-1 items-stretch gap-2' : 'items-start gap-3',
        )}
      >
        <div className="min-w-0 flex-1">
          <p
            className={clsx(
              compact
                ? 'line-clamp-2 min-h-[2rem] text-[10px] leading-tight text-slate-500 dark:text-slate-400'
                : 'text-xs text-slate-500 dark:text-slate-400 md:text-sm'
            )}
          >
            {displayTitle}
          </p>
          {showSide ? (
            <div className="mt-1.5 flex min-h-[2.75rem] items-end justify-between gap-3 md:mt-2">
              <p className="min-w-0 text-xl font-bold text-slate-800 dark:text-slate-100 md:text-2xl">{value}</p>
              <div className="shrink-0 text-right">
                <p className="text-[10px] leading-tight text-slate-400 dark:text-slate-500 md:text-xs">
                  {sideLabel ?? '当前筛选'}
                </p>
                <p className="mt-0.5 text-sm font-semibold tabular-nums text-slate-600 dark:text-slate-200 md:text-base">
                  {sideValue}
                </p>
              </div>
            </div>
          ) : (
            <p
              className={clsx(
                'inline-flex flex-wrap items-baseline gap-x-1 font-bold text-slate-800 dark:text-slate-100',
                compact
                  ? 'mt-1 text-base tabular-nums leading-none md:text-lg'
                  : 'mt-1.5 text-xl md:mt-2 md:text-2xl'
              )}
            >
              {value}
            </p>
          )}
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              {isPositive ? (
                <TrendingUp className="w-4 h-4 text-success-500" />
              ) : (
                <TrendingDown className="w-4 h-4 text-error-500" />
              )}
              <span
                className={clsx(
                  'text-sm font-medium',
                  isPositive ? 'text-success-600' : 'text-error-600'
                )}
              >
                {isPositive ? '+' : ''}{trend.value}%
              </span>
              {trend.label && (
                <span className="text-sm text-slate-400">{trend.label}</span>
              )}
            </div>
          )}
        </div>
        {icon && (
          <div
            className={clsx(
              'shrink-0 self-start',
              compact ? 'rounded-md p-1.5 [&_svg]:h-4 [&_svg]:w-4' : 'rounded-lg p-2.5 md:p-3',
              colorCfg ? `${colorCfg.bg} ${colorCfg.text}` : 'bg-primary-50 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300',
            )}
          >
            {icon}
          </div>
        )}
      </div>
      {footer && <div className="mt-3 text-xs text-slate-500 dark:text-slate-400">{footer}</div>}
    </>
  )

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={rootClassName}>
        {inner}
      </button>
    )
  }

  return <div className={rootClassName}>{inner}</div>
}

